// ---------------------------------------------------------------------------
// The header's right-hand side, entire: one avatar button and the menu behind
// it.
//
// This replaces `AccountBlock`, and the reasoning that block carried is still
// true — it has moved in here rather than gone:
//
//   - Account entry belongs in the header, not at the foot of the home screen
//     where it used to sit. AppHeader is on every screen outside a room, so
//     signing in is reachable from the browser and the solo screens too, and
//     the home screen no longer ends on a control nobody was looking for.
//   - The username lives in its own boxed mono slot and is NEVER spliced into
//     the name string. Display names may contain parentheses, so `Hyper` +
//     `@isaac` has to be structurally different from a display name that
//     literally reads `Hyper (isaac)`. PlayerChip makes the same distinction
//     the same way, and for the same reason.
//   - With Clerk unconfigured there is no sign-in affordance at all, because a
//     control that cannot possibly work is worse than no control. That is the
//     state this site ships in, not an edge case: guest play is a supported
//     mode, so it says nothing rather than apologising for itself.
//
// What is new is the shape. The old cluster was a name, a handle, a Sign out
// and a 74px theme word, all competing for the right half of a 360px header,
// and the handle was the piece that gave way: `@isaac` — five characters —
// arrived on a phone truncated to `@isa…`. Widening it was not available,
// because everything beside it was already at its minimum.
//
// So nothing out there competes any more. The header's right side is a single
// 44px button, and the handle gets a whole row inside the menu, where sixteen
// characters fit without wrapping and nothing is ever cut. It is a popover
// rather than a `<details>` for the reasons written out on `HostRowMenu`, and
// the open/close machinery below is that component's, deliberately: an outside
// `pointerdown` (captured), Escape, and focus handed back to the trigger.
//
// Two things about what is *not* in here. There is no avatar image, because
// this app never reads Clerk's user object at all — see the third numbered
// reason on `useAccount`, and the mock in `account.test.tsx` that exists
// precisely to prove `useUser` is never called. An initial drawn from the
// display name says as much as a photo would and costs no personal data. And
// the theme row does not close the menu: somebody comparing light against dark
// wants to see the change and often wants to change back.
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useRef, useState } from 'react';
import type { MouseEvent, ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { useAccount } from '../../hooks/useAccount.js';
import { usePlayerStore } from '../../store/playerStore.js';
import { useSettingsStore, type Theme } from '../../store/settingsStore.js';
import {
  HelpIcon,
  Icon,
  MoonIcon,
  MoreIcon,
  PencilIcon,
  SettingsIcon,
  SignInIcon,
  SignOutIcon,
  SunIcon,
  SystemThemeIcon,
  UserIcon,
  type IconData,
} from '../primitives/Icon.js';
import { requestUsernamePrompt } from './usernamePrompt.js';
import { SettingsDialog } from './SettingsDialog.js';

/** The one sentence worth saying about signing in, where there is room for it. */
const WHY = 'Guests play everything. Signing in keeps stats and holds a username.';

/**
 * Dark first, matching the order the header's old cycling button ran in and the
 * order the app defaults to.
 */
const THEMES: readonly { value: Theme; label: string; icon: IconData }[] = [
  { value: 'dark', label: 'Dark', icon: MoonIcon },
  { value: 'light', label: 'Light', icon: SunIcon },
  { value: 'system', label: 'System', icon: SystemThemeIcon },
];

/** The first character of a name, by code point rather than by UTF-16 unit. */
function initialOf(name: string): string | null {
  const first = Array.from(name.trim())[0];
  return first === undefined ? null : first.toUpperCase();
}

export interface AccountMenuProps {
  /**
   * Overrides the sign-in action. The home screen's prop seam still exists so a
   * caller can supply its own, but nothing has to: the action comes from the
   * account context by default.
   */
  onSignIn?: () => void;
  /**
   * Vetoes any navigation out of this menu. Return false to keep the player
   * where they are; the room passes `homeGuard`, which asks before abandoning a
   * game in progress.
   *
   * Every link in here is a way out of a running round, and "How to play" is
   * the one a player reaches for mid-round precisely because they are stuck. A
   * menu of unguarded exits sitting behind the only button in the header is a
   * game thrown away on one tap.
   */
  leaveGuard?: () => boolean;
}

export function AccountMenu({ onSignIn, leaveGuard }: AccountMenuProps): ReactElement {
  const account = useAccount();
  const localName = usePlayerStore((store) => store.displayName);
  const theme = useSettingsStore((store) => store.theme);
  const setTheme = useSettingsStore((store) => store.setTheme);

  const [open, setOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  // Outside the close effect on purpose: closing is what tears that effect
  // down, and a timer cleared by its own cause never runs.
  const restoreRef = useRef(0);
  useEffect(() => () => window.clearTimeout(restoreRef.current), []);

  const details = account.account;
  const signedIn = account.enabled && account.ready && account.signedIn;
  // An account has no display name of its own until the username is claimed, so
  // until then the name they are already playing under is the honest one.
  const shownName = (details?.displayName.trim() ?? '') || localName.trim();
  const canSignIn = account.enabled || onSignIn !== undefined;

  const close = useCallback(() => {
    setOpen(false);
    triggerRef.current?.focus();
  }, []);

  // Closes on the way out, and stays open when the guard says no — a menu that
  // shut itself while the "leave the game?" dialogue was going up would answer
  // the question before it was asked. Preventing the default stops this
  // navigation and not the link, so the item is still a real URL to open in a
  // new tab.
  const onLeave = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      if (leaveGuard !== undefined && !leaveGuard()) {
        event.preventDefault();
        return;
      }
      close();
    },
    [leaveGuard, close],
  );

  useEffect(() => {
    if (!open) return;

    // `pointerdown`, not `click`: a press that begins outside the menu already
    // means "not this menu", and waiting for the click leaves it open under a
    // finger that has started a scroll. Captured, so an item that stops
    // propagation cannot keep the menu alive.
    const onPointerDown = (event: PointerEvent) => {
      if (rootRef.current?.contains(event.target as Node) === true) return;
      setOpen(false);
      // Focus goes back a task later rather than here. The browser's own press
      // handling moves focus to whatever was pressed — to the body, when that
      // is nothing focusable — and it does that *after* this listener runs. It
      // goes back only if nothing else claimed it: somebody who pressed another
      // control should be left in that control.
      restoreRef.current = window.setTimeout(() => {
        if (document.activeElement === document.body) triggerRef.current?.focus();
      }, 0);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };

    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, close]);

  // Focus lands on the first thing in the menu, so Enter or Space on the avatar
  // leaves a keyboard where the choices are rather than where the button was.
  useEffect(() => {
    if (!open) return;
    panelRef.current?.querySelector<HTMLElement>('a[href], button')?.focus();
  }, [open]);

  // One geometry for every row, so an item is the same size whether it is a
  // link or a button. 44px tall, like everything else a thumb has to hit.
  const item =
    'flex min-h-11 w-full items-center gap-2.5 rounded-chip px-3 text-left text-[13px] font-semibold text-ink no-underline transition-colors duration-150 ease-out hover:bg-raised';
  const divider = <div aria-hidden="true" className="mx-2 my-1 h-px bg-line" />;

  const initial = signedIn ? initialOf(shownName) : null;

  return (
    <div ref={rootRef} className="relative shrink-0">
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        // Names the menu, and names who it belongs to when there is somebody to
        // name. A screen reader on a shared page should not have to open it to
        // find out whether anyone is signed in.
        aria-label={signedIn && shownName ? `Menu for ${shownName}` : 'Menu'}
        onClick={() => (open ? close() : setOpen(true))}
        className="grid h-11 w-11 cursor-pointer place-items-center rounded-full transition-colors duration-150 ease-out"
      >
        <span
          aria-hidden="true"
          className="grid h-8 w-8 place-items-center rounded-full border border-line bg-surface-2 text-[13px] font-bold text-ink transition-colors duration-150 ease-out hover:border-line-strong"
        >
          {initial ?? (
            // A person glyph where there is an account to sign into, and the
            // three dots where there is not: on a deployment with no Clerk key
            // this button opens theme and how-to-play and nothing else, and a
            // person would be promising an account that does not exist.
            <Icon icon={canSignIn ? UserIcon : MoreIcon} size={16} className="text-ink-dim" />
          )}
        </span>
      </button>

      {open ? (
        <div
          ref={panelRef}
          // `right-0`, and never wider than the viewport less the header's own
          // gutters, so the far edge of a phone is the one thing it cannot run
          // past. It scrolls rather than growing taller than the screen.
          className="absolute top-full right-0 z-30 mt-1 flex max-h-[calc(100dvh-4.5rem)] w-64 max-w-[calc(100vw-2rem)] flex-col gap-0.5 overflow-y-auto rounded-card border border-line bg-surface p-1.5"
        >
          {signedIn ? (
            <div className="flex flex-col items-start gap-1.5 px-3 pt-2 pb-2.5">
              {shownName ? (
                <span className="max-w-full text-[14px] font-semibold break-words text-ink">
                  {shownName}
                </span>
              ) : null}
              {details?.username != null ? (
                // The whole handle. It has a row to itself here, which is the
                // entire reason the menu exists, so nothing truncates it.
                <span className="max-w-full rounded-[5px] bg-raised px-1.5 py-1 font-mono text-[12px] break-all text-ink-dim">
                  @{details.username}
                </span>
              ) : (
                // Signed in with no username yet, or a session we could verify
                // and an account we could not read. Neither is worth a sentence
                // up here, and the first has an item of its own below.
                <span className="label-mono" title={details === null ? (account.error ?? undefined) : undefined}>
                  {details === null ? 'signed in' : 'no username'}
                </span>
              )}
            </div>
          ) : null}

          {!signedIn && canSignIn ? (
            <button
              type="button"
              title={WHY}
              onClick={() => {
                close();
                (onSignIn ?? account.signIn)();
              }}
              className={item}
            >
              <Icon icon={SignInIcon} size={16} />
              Sign in
            </button>
          ) : null}

          {signedIn && details?.username != null ? (
            <Link to={`/u/${details.username}`} onClick={onLeave} className={item}>
              <Icon icon={UserIcon} size={16} />
              Profile
            </Link>
          ) : null}

          {signedIn && details !== null && details.username === null ? (
            // The onboarding dialog offers this on the way in and takes "Later"
            // for an answer, which used to leave no way back to it at all.
            <button
              type="button"
              onClick={() => {
                close();
                requestUsernamePrompt();
              }}
              className={item}
            >
              <Icon icon={PencilIcon} size={16} />
              Pick a username
            </button>
          ) : null}

          <Link to="/how-to-play" onClick={onLeave} className={item}>
            <Icon icon={HelpIcon} size={16} />
            How to play
          </Link>

          {/* Not a link, so `leaveGuard` has nothing to say about it: a modal
              over the current screen leaves no game. */}
          <button
            type="button"
            onClick={() => {
              close();
              setSettingsOpen(true);
            }}
            className={item}
          >
            <Icon icon={SettingsIcon} size={16} />
            Settings
          </button>

          {divider}

          <div className="px-3 pt-1 pb-1.5">
            <span className="label-mono">theme</span>
          </div>
          <div role="radiogroup" aria-label="Theme" className="grid grid-cols-3 gap-1 px-1.5 pb-1">
            {THEMES.map((option) => {
              const on = theme === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  onClick={() => setTheme(option.value)}
                  className={`flex min-h-11 flex-col items-center justify-center gap-1 rounded-chip text-[11px] font-semibold transition-colors duration-150 ease-out ${
                    // The palette's one solved pairing for text on a filled
                    // blue. Both fills are opaque, so nothing composites here.
                    on ? 'bg-blue-500 text-ink-on-fill' : 'text-ink-muted hover:bg-raised hover:text-ink'
                  }`}
                >
                  <Icon icon={option.icon} size={16} />
                  {option.label}
                </button>
              );
            })}
          </div>

          {signedIn ? (
            <>
              {divider}
              <button
                type="button"
                onClick={() => {
                  close();
                  account.signOut();
                }}
                className={item}
              >
                <Icon icon={SignOutIcon} size={16} />
                Sign out
              </button>
            </>
          ) : null}
        </div>
      ) : null}

      {/* Rendered here rather than at the app root because this is the only
          thing that opens it. It is `position: fixed`, so being inside the
          header's stacking context costs it nothing. */}
      <SettingsDialog open={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </div>
  );
}
